import React, { useState } from 'react';
import { Artist, isSuperAdmin } from '../types';
import { API_CONFIG } from '../config';
import { apiFetch } from '../utils/api';
import { useTranslation } from '../hooks/useTranslation';
import Input from './ui/Input';
import Button from './ui/Button';
import { ICONS } from '../constants';

interface ProfileAccessSectionProps {
  profile: Artist;
  onUpdated?: (profile: Artist) => void;
  compact?: boolean;
  currentUserRole?: string | null;
}

const ProfileAccessSection: React.FC<ProfileAccessSectionProps> = ({
  profile,
  onUpdated,
  compact = false,
  currentUserRole,
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const resolvedRole =
    currentUserRole ||
    (() => {
      try {
        const raw = localStorage.getItem('spellbook_user');
        return raw ? (JSON.parse(raw) as { role?: string }).role || null : null;
      } catch {
        return null;
      }
    })();

  const hasLogin = Boolean(profile.userId);
  const isPending = profile.registrationStatus === 'pending';
  const sa = isSuperAdmin(resolvedRole);
  const showAccess =
    profile.profileType !== 'vfx_artist' || hasLogin || isPending;

  if (!showAccess) return null;

  const handleApprove = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/artists/${profile.id}/approve`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to approve');
      onUpdated?.(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (password.length < 8) {
      setError(t('register.passwordTooShort'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/artists/${profile.id}/grant-access`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to grant access');
      onUpdated?.(data.profile);
      setPassword('');
      setShowGrantForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/access-requests`, {
        method: 'POST',
        body: JSON.stringify({
          profileId: profile.id,
          claimedProfileType: profile.profileType || 'producer',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to request access');
      setRequestSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAccess = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_CONFIG.API_URL}/artists/${profile.id}/revoke-access`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to revoke access');
      onUpdated?.(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    setLoading(true);
    setError('');
    try {
      const kind = profile.profileType === 'client' ? 'client' : 'staff';
      const response = await apiFetch(`${API_CONFIG.API_URL}/invite/create`, {
        method: 'POST',
        body: JSON.stringify({
          kind,
          profileType: profile.profileType,
          targetProfileId: profile.id,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create invite');
      setInviteUrl(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteUrl = () => {
    if (inviteUrl) navigator.clipboard.writeText(inviteUrl);
  };

  return (
    <div className={`${compact ? 'mt-4' : 'mt-6'} border-t border-gray-700 pt-4`}>
      <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
        <ICONS.Lock className="w-4 h-4" />
        {t('profile.access.title')}
      </h4>

      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
      {requestSent && (
        <p className="text-green-400 text-sm mb-2">{t('profile.access.requestSent')}</p>
      )}

      <p className="text-sm text-gray-400 mb-3">
        {hasLogin ? t('profile.access.hasLogin') : t('profile.access.noLogin')}
      </p>

      <div className="flex flex-wrap gap-2">
        {isPending && (
          <Button size="sm" onClick={handleApprove} disabled={loading}>
            {t('profile.access.approve')}
          </Button>
        )}

        {!hasLogin && !isPending && sa && (
          <>
            <Button size="sm" variant="secondary" onClick={() => setShowGrantForm(!showGrantForm)}>
              {t('profile.access.grantPassword')}
            </Button>
            <Button size="sm" variant="secondary" onClick={handleCreateInvite} disabled={loading}>
              {t('profile.access.createInvite')}
            </Button>
          </>
        )}

        {!hasLogin && !isPending && !sa && (
          <>
            <Button size="sm" variant="secondary" onClick={handleRequestAccess} disabled={loading || requestSent}>
              {t('profile.access.requestAccess')}
            </Button>
            <Button size="sm" variant="secondary" onClick={handleCreateInvite} disabled={loading}>
              {t('profile.access.createInvite')}
            </Button>
          </>
        )}

        {hasLogin && (
          <Button size="sm" variant="danger" onClick={handleRevokeAccess} disabled={loading}>
            {t('profile.access.revokeLogin')}
          </Button>
        )}
      </div>

      {showGrantForm && !hasLogin && sa && (
        <div className="mt-3 space-y-2">
          <Input
            type="password"
            label={t('login.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button size="sm" onClick={handleGrantAccess} disabled={loading}>
            {t('profile.access.grantPassword')}
          </Button>
        </div>
      )}

      {inviteUrl && (
        <div className="mt-3 flex items-center gap-2 min-w-0">
          <input
            readOnly
            value={inviteUrl}
            className="flex-1 min-w-0 max-w-full box-border text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded border border-gray-600 truncate"
          />
          <Button size="sm" variant="secondary" onClick={copyInviteUrl}>
            {t('profile.access.copyLink')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfileAccessSection;
